import { ChangeType } from "../globals"

export type Maybe<T> = T | null | undefined

export type MaybeObject<T> = {
  [K in keyof T]?: Maybe<T[K]>
}

type RequiredObject<T> = {
  [K in keyof T]-?: NonNullable<T[K]>
}

export enum ReasonForChange {
  NEW_DOCUMENT = 1,
  PERIODICAL_REVIEW = 2,
  REQUIREMENT_CHANGE_DO_RADDED = 3,
  PROCESS_CHANGED = 4,
  ACCOUNTABILITY_CHANGED = 5,
  SIGNIFICANT_CHANGE_IN_DATA = 6,
  NEW_LEGAL_REQUIREMENT = 7,
  AUDIT_RECOMMENDATIONS = 8,
  DOCUMENT_RETIREMENT = 9,
}

export type RefinerSetting = {
  name: string
  code: string
}

export type Configurator = {
  id: string
  type: string
  managerName: string
  managerEmail: string
  owner: string
  tagA: string
  tagB: string
  tagC: string
  configuratorCountry: string
  configuratorType: string
  availableActions?: string[]
  primaryLocalLanguage: string
  secondaryLocalLanguage: string
  tertiaryLocalLanguage: string
}

export type ContentOwner = {
  name: string
  emailAddress: string
}

export type ContentRole = {
  configuratorId: string
  itemId: string
  primaryLanguageItemOwners: ContentOwner[]
  secondaryLanguageItemOwners: ContentOwner[]
  tertiaryLanguageItemOwners: ContentOwner[]
}

export type LinksConfig = {
  conditionalList: ConditionalLink[]
  staticList: TitledLink[]
}

export type BaseEditingBlock = RequiredObject<OpfBaseBlock> & {
  archived: boolean
}

export type EditingSubsection = BaseEditingBlock & {
  type: "subsection"
}

export type EditingSection = BaseEditingBlock & {
  type: "section"
  subsections: EditingSubsection[]
}

export type EditingChapter = BaseEditingBlock & {
  type: "chapter"
  sections: EditingSection[]
}

export type EditingBlock = EditingChapter | EditingSection | EditingSubsection

export type CommonDocumentFormValues = {
  documentControl: OpfDocumentControl | null
  documentOwner: string
  informationContact: string
  lastPageText: string
  text: string
  title: string
  chapters: EditingChapter[]
}

export type EditingFormValues = {
  classification: number | null
  documentNumber: string
  generateUniqueNumber: boolean
  globalType: "global" | "group" | null
  viewableByExternals: boolean
} & CommonDocumentFormValues

export type TranslatingFormValues = CommonDocumentFormValues

export type DocumentFormValues = EditingFormValues | TranslatingFormValues

export const isEditingFormValues = (
  formValues: DocumentFormValues
): formValues is EditingFormValues => "viewableByExternals" in formValues

export type PublishingFormValues = {
  nextReviewDate: string | null
  nextReviewDateIsApplicable: boolean
  editorialVersion: string
  approvedDate: string | null
  approvedBy: string | null
  approvalId: string | null
  links: LinksConfig
  supportingDocuments: LinksConfig
  pdfDocuments: LinksConfig
  relatedDocuments: RelatedDocumentItem[]
  relatedProcesses: RelatedDocumentItem[]
  updateType: "major" | "minor" | null
  changeType: ChangeType | null
  reasonForChange: ReasonForChange | null
  changeLog: string | null
}

export interface IOpfItemStep extends OpfBase {
  new?: boolean
  removed?: boolean
}

export type ItemFormData = {
  title: string
  icon?: string
  viewableByExternals?: boolean
  classification?: number
  lastPageText?: number
  nextReviewDate?: string
  nextReviewDateIsApplicable?: boolean
  accessRoles?: string[]
  text: string
  procedureMediaFiles?: {
    fileList: OpfMediaFile[]
  }
  isMajorUpdate?: boolean
  notification: OpfNotification & {
    editorialVersion?: string
  }
  steps?: OpfBase[]
  editorialVersion?: string
  documentOwner?: string
  informationContact?: string
  globalType?: string
  approvalId?: string
  approvedBy?: string
  approvedDate?: string
  documentNumber?: string
}

export type OpfMediaFile = { url: string } & File

export type OpfNotification = MaybeObject<{
  changeLog: string
  changeTo: string
  changeType: string
  howTo: string
  isMajorChange: boolean
  reasonForChange: ReasonForChange
}>

export type TitledLink = {
  url: string
  title: string
}

export type ConditionalLink = {
  enabled?: boolean
} & TitledLink

export type RelatedDocument = {
  title: string
  url: string
  type: string
}

export type RelatedDocumentItem = {
  slug: string
  opfId: string
}

export type Media = {
  title?: string
  url?: string
  archived?: boolean
  internal?: boolean
  thumbnail?: string
}

export type Item = {
  opfId: string
  chapters?: []
}

export type OpfItem = {
  steps?: OpfBase[]
  mediaLibrary?: Media[]
  opfVersionId?: string
  documentVersionId?: string
  redirectSlug?: string
} & OpfBase

type OpfBaseBlock = {
  id: string
  translationReferenceId?: Maybe<string>
  title?: Maybe<string>
  text?: Maybe<string>
}

export type OpfSubsection = { type: "subsection" } & OpfBaseBlock

export type OpfSection = {
  type: "section"
  subsections: Maybe<OpfSubsection[]>
} & OpfBaseBlock

export type OpfChapter = {
  type: "chapter"
  sections: Maybe<OpfSection[]>
} & OpfBaseBlock

export type OpfBlock = OpfChapter | OpfSection | OpfSubsection
export type OpfBlockType = OpfBlock["type"]

export type OpfDocumentControl = {
  documentOwner?: string
  documentReviewer?: string
  documentApprover?: string
  effectiveFrom?: string
  reviewDate?: string
  translationRequired?: boolean
  contactPoint?: string
}

export type OpfDocument = OpfBase &
  MaybeObject<{
    cardImageUrl: string
    chapters: OpfChapter[]
    documentAsFile: RelatedDocument
    documentControl: OpfDocumentControl
    documentVersionId: string
    draftVersion: OpfDocument
    fallbackVersion: OpfDocument
    opfDocumentChildren: OpfDocument[]
    redirectSlug: string
    relatedDocuments: string[]
    relatedDocumentsLinks: RelatedDocument[]
    relatedProcesses: string[]
    relatedProcessesLinks: RelatedDocument[]
  }>

export type OpfBase = {
  id: string
  type: string
  tree: string
  slug: string
  opfId: string
  version: number
  majorVersion: number
  archiveStep?: boolean
} & MaybeObject<{
  accessRoles: string[]
  approvalId: string
  approvedBy: string
  approvedDate: string
  archived: boolean
  availableActions: string[]
  classification: number
  configurators: string[]
  contact: any
  content: string
  createdBy: string
  createdDate: string
  creatorTagA: string
  creatorTagAa: string
  creatorTagB: string
  creatorTagC: string
  documentNumber: string
  documentOwner: string
  draft: boolean
  draftVersion: OpfItem
  editorialVersion: string
  expandable: boolean
  fallbackVersion: OpfItem
  function: string
  global: boolean
  globalType: string
  hasDocumentNumber: boolean
  icon: string
  informationContact: string
  initialLanguage: string
  language: string
  lastPageText: string
  level: number
  links: Media[]
  name: string
  nextReviewDate: string
  nextReviewDateIsApplicable: boolean
  nextReviewDateIsNotApplicable: boolean
  notification: OpfNotification
  opfChildren: OpfBase[]
  order: number
  parentId: string
  sourceConfigurator: string
  stepId: string
  tagA: string
  tagAa: string
  tagB: string
  tagC: string
  text: string
  title: string
  translated: boolean
  updatedBy: string
  updatedDate: string
  userIsSubscribed: boolean
  viewableByExternals: boolean
}>

export type Draft = {
  slug: string
  printableSlug: string
  id: string
  opfId: string
  title: string
  owner?: string
  lastUpdatedBy?: string
  lastUpdatedDate?: string
  version: number
}

export type Document = {
  global: boolean
  id: string
  language: `${string}${string}`
  level: number
  name: string
  opfId: string
  opfVersionId?: string | null
  order: number
  parentId: string
  slug: string
  title: string
  type: string
}
